/*
 * api/client.js — thin fetch wrapper for the SellerSignal v3 backend.
 *
 * In development, Vite's proxy sends /api/* to http://localhost:8000.
 * In production, frontend is served from the same origin as the backend.
 */

import { getAccessToken } from '../lib/supabase.js';

const API_BASE = '/api';

async function request(path, options = {}) {
  const { headers: optHeaders, ...rest } = options;
  const url = `${API_BASE}${path}`;
  const resp = await fetch(url, {
    credentials: 'include',
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(optHeaders || {}),
    },
  });

  if (!resp.ok) {
    const text = await resp.text();
    let detail;
    try { detail = JSON.parse(text); } catch { detail = text; }
    const err = new Error(`API error ${resp.status}: ${resp.statusText}`);
    err.status = resp.status;
    err.detail = detail;
    throw err;
  }

  return resp.json();
}

/**
 * safeErrorMessage — coerce an arbitrary error (especially the
 * structured detail blob FastAPI returns on 422 validation errors)
 * into a string suitable for rendering as a React child.
 *
 * FastAPI 422 shape is: {"detail": [{"type", "loc", "msg", "input"}]}
 * Rendering that array directly causes React error #31. This helper
 * extracts the first .msg from the array, or falls through to the
 * usual string-detail / err.message paths.
 */
export function safeErrorMessage(err, fallback = 'Something went wrong') {
  const detail = err?.detail?.detail ?? err?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) {
    // Surface the first validation error in human form
    const loc = Array.isArray(detail[0].loc) ? detail[0].loc.join('.') : '';
    return loc ? `${detail[0].msg} (${loc})` : detail[0].msg;
  }
  if (typeof detail === 'object' && detail?.message) return detail.message;
  return err?.message || fallback;
}

/**
 * authedRequest — same as request() but auto-attaches the Supabase
 * access token as a Bearer header. Used for endpoints that require
 * authentication (lead interactions, profile, etc.). If the user
 * isn't signed in, throws an error with status=401 BEFORE making
 * the fetch call so the caller can handle "needs auth" cleanly
 * (e.g., open the conversion modal in cold-visitor mode).
 */
async function authedRequest(path, options = {}) {
  const token = await getAccessToken();
  if (!token) {
    const err = new Error('Not signed in');
    err.status = 401;
    err.detail = { message: 'Authentication required' };
    throw err;
  }
  return request(path, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
}

// ── Marketplace (buyer network — dark launch, allowlist-gated) ─────
// Every endpoint 404s unless the signed-in email is on the server
// allowlist. Treat 404 here as "feature does not exist for this user."

export const network = {
  status:  ()            => authedRequest('/marketplace/status'),
  filters: (zips)        => authedRequest(`/marketplace/filters?zips=${encodeURIComponent(zips)}`),
  needs:   ()            => authedRequest('/marketplace/needs'),
  need:    (id)          => authedRequest(`/marketplace/needs/${id}`),
  createNeed: (body)     => authedRequest('/marketplace/needs', { method: 'POST', body: JSON.stringify(body) }),
  patchNeed:  (id, body) => authedRequest(`/marketplace/needs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  runMatch:   (id)       => authedRequest(`/marketplace/needs/${id}/match`, { method: 'POST' }),
  report:     (id, q='') => authedRequest(`/marketplace/needs/${id}/report${q}`),
  demand:     (zip)      => authedRequest(`/marketplace/demand/${zip}`),
  respond:    (zip, needId, action) => authedRequest(
    `/marketplace/demand/${zip}/${needId}/respond?action=${action}`, { method: 'POST' }),
  connect:    (zip, needId) => authedRequest(
    `/marketplace/demand/${zip}/${needId}/connect`, { method: 'POST' }),
  join:       (body)     => authedRequest('/marketplace/join', { method: 'POST', body: JSON.stringify(body) }),
  joinStatus: ()         => authedRequest('/marketplace/join/status'),
  rate:       (zip, needId, rating, real) => authedRequest(
    `/marketplace/demand/${zip}/${needId}/rate?rating=${rating}&client_was_real=${real}`,
    { method: 'POST' }),
};

// ── Coverage ───────────────────────────────────────────────────────

export const coverage = {
  list: () => request('/coverage'),
  detail: (zip) => request(`/coverage/${zip}`),
  stats:  (zip) => request(`/coverage/${zip}/stats`),
};

// ── ZIP polygons (for the territories map) ─────────────────────────

export const zipPolygons = {
  // Returns a GeoJSON FeatureCollection — one polygon per live ZIP
  // in our coverage. The frontend caches this aggressively; the
  // browser also honors the server's Cache-Control: max-age=3600.
  list: () => request('/zip-polygons'),
};

// ── Notify-me when a territory releases ────────────────────────────

export const notifications = {
  subscribe: (zip_code, email, source = 'territories_map') =>
    request('/notifications/subscribe', {
      method: 'POST',
      body: JSON.stringify({ zip_code, email, source }),
    }),
  queueSize: (zip) => request(`/notifications/zip/${zip}/queue-size`),
};

// ── Public ZIP availability (homepage checker) ────────────────────

export const availability = {
  check: (zip) => request(`/coverage/availability/${zip}`),
};

// ── Briefings ──────────────────────────────────────────────────────

export const briefings = {
  get: (zip, includeMap = true) =>
    authedRequest(`/briefings/${zip}?include_map=${includeMap}`),
  summary: (zip) => authedRequest(`/briefings/${zip}/summary`),
  history: (zip, limit = 12) => authedRequest(`/briefings/${zip}/history?limit=${limit}`),
};

// ── Map data ───────────────────────────────────────────────────────

export const map = {
  get: (zip, includeUninvestigated = true) =>
    // slim=1 drops address/owner/value (fetched on click via the dossier)
    // so the WHOLE zip fits in one response — the old 5000-row default
    // truncated 81 of 100 territories into an arbitrary subset of dots.
    authedRequest(`/map/${zip}?include_uninvestigated=${includeUninvestigated}&slim=1`),
  bounds: (zip) => authedRequest(`/map/${zip}/bounds`),
  streetView: (pin, size = '640x400') =>
    authedRequest(`/map/streetview/${pin}?size=${size}`),
};

// ── Parcels ────────────────────────────────────────────────────────

export const parcels = {
  get: (pin) => authedRequest(`/parcels/${pin}`),
  why: (pin) => authedRequest(`/parcels/${pin}/why`),
};

// ── Investigations ─────────────────────────────────────────────────

export const investigations = {
  run: (zip, dryRun = true, maxFinalists = 15) =>
    request('/investigations/run', {
      method: 'POST',
      body: JSON.stringify({ zip_code: zip, dry_run: dryRun, max_finalists: maxFinalists }),
    }),
  budget: () => request('/investigations/budget'),
  deep: (pin) => request(`/investigations/parcel/${pin}/deep`, { method: 'POST' }),
};

// ── Playbook ───────────────────────────────────────────────────────

export const playbook = {
  get: (zip) => request(`/playbook/${zip}`),
  pdfUrl: (zip) => `${API_BASE}/playbook/${zip}/pdf`,  // direct <a href> target
};

// ── Deep Signal ────────────────────────────────────────────────────
// GET returns cached Deep Signal or 404 — use to check existence.
// POST generates on demand (cache-first; pass force=true to bypass cache).

export const deepSignal = {
  get:       (pin) => request(`/deep-signal/${pin}`),
  generate:  (pin, force = false) =>
    request(`/deep-signal/${pin}${force ? '?force=true' : ''}`, { method: 'POST' }),
};

// ── Health ─────────────────────────────────────────────────────────

export const health = {
  check: () => request('/health'),
  status: () => request('/status'),
};

// ── Lead Interactions (Lead Memory) ────────────────────────────────
// Per-agent event log: working / not_relevant / sent_to_crm + outcomes.
// All endpoints require auth — calls throw 401 in cold-visitor mode
// before the fetch fires, which the dossier handles by routing to
// the conversion modal.

export const leadInteractions = {
  /**
   * Log a new event for the current agent.
   * @param {{pin: string, zip_code: string, event_type: string, event_data?: object}} body
   */
  log: (body) => authedRequest('/lead-interactions', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * Full event history for one parcel from the current agent.
   * Returns {pin, events: [{event_type, event_data, created_at, ...}, ...]}
   * — newest first.
   */
  byPin: (pin) => authedRequest(`/lead-interactions/by-pin/${pin}`),

  /**
   * Per-pin current status map for a whole ZIP. Returns
   * {zip_code, statuses: {pin: {status, status_at, event_data}}}.
   * Reads from lead_status_v3 view — only includes pins with at
   * least one status-changing event from this agent.
   */
  byZip: (zip) => authedRequest(`/lead-interactions/by-zip/${zip}`),
};


// ── Lead notes ────────────────────────────────────────────────────
// Mutable free-text notes per (agent, parcel). Multiple notes per
// parcel allowed; each has its own id, body, and created/updated
// timestamps. Backed by lead_notes_v3 (migration 019).

export const leadNotes = {
  /**
   * Create a new note. Returns the inserted row.
   * @param {{pin: string, zip_code: string, body: string}} body
   */
  create: (body) => authedRequest('/lead-notes', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * Update an existing note's body. Server auto-bumps updated_at.
   * @param {string} id
   * @param {string} body
   */
  update: (id, body) => authedRequest(`/lead-notes/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ body }),
  }),

  /**
   * Delete a note. Returns {deleted: true, id}. RLS scoped to caller.
   */
  remove: (id) => authedRequest(`/lead-notes/${id}`, {
    method: 'DELETE',
  }),

  /**
   * List all this agent's notes on one parcel, newest first.
   * Returns {pin, notes: [{id, body, created_at, updated_at, ...}, ...]}
   */
  byPin: (pin) => authedRequest(`/lead-notes/by-pin/${pin}`),
};


// ── Lead tags ─────────────────────────────────────────────────────
// Flat (agent, pin, zip, tag) assignments. Agent's distinct tag
// strings form their own private taxonomy — no shared taxonomy
// table. Backed by lead_tags_v3 (migration 019).

export const leadTags = {
  /**
   * Assign a tag to a parcel. Idempotent: re-adding an existing
   * (pin, tag) returns the existing row rather than erroring.
   * @param {{pin: string, zip_code: string, tag: string}} body
   */
  create: (body) => authedRequest('/lead-tags', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * Remove a tag assignment by id.
   */
  remove: (id) => authedRequest(`/lead-tags/${id}`, {
    method: 'DELETE',
  }),

  /**
   * List this agent's distinct tags with usage counts.
   * Optionally filter by zip_code. Returns
   * {tags: [{tag, count}, ...]} sorted by count desc, then alpha.
   * Powers the briefing-page tag filter chip row.
   */
  list: (zip_code) => {
    const qs = zip_code ? `?zip_code=${encodeURIComponent(zip_code)}` : '';
    return authedRequest(`/lead-tags${qs}`);
  },

  /**
   * All tags this agent has on one parcel.
   * Returns {pin, tags: [{id, tag, created_at, ...}, ...]}.
   */
  byPin: (pin) => authedRequest(`/lead-tags/by-pin/${pin}`),

  /**
   * All pins this agent has assigned a given tag to.
   * Optionally filter by zip_code. Returns
   * {tag, zip_code, assignments: [{pin, zip_code, created_at, ...}, ...]}.
   * Powers "search by tag" in the briefing UI.
   */
  byTag: (tag, zip_code) => {
    const qs = zip_code ? `?zip_code=${encodeURIComponent(zip_code)}` : '';
    return authedRequest(`/lead-tags/by-tag/${encodeURIComponent(tag)}${qs}`);
  },
};


// ── My Leads (active pipeline) ────────────────────────────────────
// Aggregated view: every parcel this agent has any engagement on
// (interaction, note, or tag), minus pins dismissed via not_relevant
// or closed. Backed by GET /api/my-leads.

export const myLeads = {
  /**
   * Returns the agent's full active pipeline:
   *   {
   *     leads: [{pin, zip_code, address, owner_name, status, tags,
   *              notes_count, last_action_at, last_action_type, ...}, ...],
   *     available_tags: [{tag, count}, ...],
   *     totals: {working, listing_discussion, engaged, total}
   *   }
   * Sorted newest-touched-first by the server.
   */
  list: () => authedRequest('/my-leads'),
};


// ── Skip-trace ────────────────────────────────────────────────────
// Wraps the Tracerfy-backed lookup with caching, monthly cap, and
// TCPA acknowledgment. Backed by /api/skip-trace/* endpoints.

export const skipTrace = {
  /**
   * Returns the agent's current eligibility:
   *   {acked, ack_version, monthly_used, monthly_cap,
   *    monthly_remaining, monthly_resets_at}
   * UI calls this on dossier mount to decide whether to show the
   * TCPA modal vs. proceed straight to the trace button.
   */
  status: () => authedRequest('/skip-trace/status'),

  /**
   * Record the one-time TCPA / DNC acknowledgment. Idempotent.
   * Returns {acked, ack_version, acked_at}.
   */
  ackCompliance: () => authedRequest('/skip-trace/ack-compliance', {
    method: 'POST',
  }),

  /**
   * Run a skip-trace on a parcel. Returns either a cached or fresh
   * result; both shapes are identical except for the `source` field.
   * Response: {source, hit, credits_deducted, persons,
   *            retrieved_at, expires_at, monthly_used, monthly_cap}
   *
   * Throws on 412 (not ack'd), 429 (cap reached), 502 (provider error).
   * The caller is expected to handle each cleanly — see SkipTracePanel.
   *
   * Throws 409 when the server refuses to bill again: detail.code is
   * 'refresh_cooldown' (with detail.next_refresh_at) or
   * 'trace_in_progress'. A failed refresh with a stored result returns
   * 200 with refresh_failed:true and the earlier result.
   *
   * @param {string} pin
   * @param {{force_refresh?: boolean}} [opts] — pass force_refresh:true
   *   from "Trace again" (only offered after 30 days) to run a fresh
   *   trace (spends credits, counts against monthly cap).
   */
  lookup: (pin, opts = {}) => authedRequest('/skip-trace/lookup', {
    method: 'POST',
    body: JSON.stringify({
      pin,
      force_refresh: !!opts.force_refresh,
    }),
  }),

  /**
   * Pure cache read — returns the stored trace for this parcel if one
   * exists, at any age. Never calls the provider, never spends
   * credits, never logs an event.
   *
   * Response: {cached: true, source, hit, persons, retrieved_at,
   *            expires_at, next_refresh_at, refresh_available}
   *           or {cached: false}.
   *
   * Used by the dossier on mount to auto-render existing skip-trace
   * results without making the agent click again.
   */
  cached: (pin) => authedRequest(`/skip-trace/cached/${pin}`),
};


/**
 * agentVoice — agent voice product API.
 *
 * generateScripts() runs the 6-archetype LLM generation against the
 * agent's profile (voice_sample, stance, bio). Server-side: ~30-90
 * seconds, runs all 6 in parallel. Returns the full result with
 * scripts, errors per archetype if any, token usage, and the
 * voice_onboarding_completed_at timestamp.
 *
 * editScript({archetype, script}) saves an agent-edited version of
 * one archetype's full script object back to the profile.
 */
export const agentVoice = {
  generateScripts: () => authedRequest('/agent/generate-scripts', {
    method: 'POST',
    body: JSON.stringify({}),
  }),

  editScript: ({ archetype, script }) => authedRequest('/agent/edit-script', {
    method: 'PUT',
    body: JSON.stringify({ archetype, script }),
  }),
};


/**
 * territory — territory claim & gating API.
 *
 * status() returns the territory grid annotated for the authenticated
 * user: their role, their assigned ZIP (if any), and every live ZIP
 * with a status — 'mine' | 'available' | 'claimed_by_other'.
 *
 * claim({zip_code}) attempts to claim a ZIP. Validates server-side:
 * agent role required, no prior claim, ZIP must be live and unclaimed.
 * Returns 409 on conflicts (already claimed, ZIP taken, etc.).
 */
export const territory = {
  status: () => authedRequest('/agent/territory-status'),

  claim: (zip_code) => authedRequest('/agent/claim-zip', {
    method: 'POST',
    body: JSON.stringify({ zip_code }),
  }),
};


// ── Billing / Stripe subscriptions ────────────────────────────────
// Territory subscriptions are $299/month with a 90-day commitment
// (3 monthly charges, then auto-cancel unless the agent renews).
// Renewal opens 30 days before commitment end and extends cancel_at
// by another 90 days.
//
// The bypass-claim path at /agent/claim-zip still exists for beta
// agents and admin use — but the agent-facing flow now goes through
// Stripe Checkout via createCheckout below.

export const billing = {
  /**
   * Create a Stripe Checkout Session for a territory subscription.
   * Returns { checkout_url } — frontend should redirect window.location
   * to it. Throws if ZIP isn't live, agent already has a territory, or
   * ZIP is already claimed.
   */
  createCheckout: (zip_code, opts = {}) => authedRequest('/billing/create-checkout', {
    method: 'POST',
    body: JSON.stringify({
      zip_code,
      success_path: opts.success_path,
      cancel_path: opts.cancel_path,
    }),
  }),

  /**
   * Open the Stripe Customer Portal for subscription management
   * (update card, view invoices, cancel). Returns { portal_url }.
   * Throws 404 if the agent has no Stripe customer on file (beta agents
   * and operators don't — only paid agents who completed checkout).
   */
  portalLink: (return_path) => authedRequest('/billing/portal-link', {
    method: 'POST',
    body: JSON.stringify({ return_path }),
  }),

  /**
   * Returns the agent's current subscription state. Used by ProfilePage
   * to show next-billing-date and decide whether to surface the Renew
   * button (only visible when within 30 days of cancel_at).
   * Response: { has_subscription, zip_code, status, current_period_end,
   *             cancel_at, days_until_cancel, eligible_for_renewal }
   */
  subscriptionStatus: () => authedRequest('/billing/subscription-status'),

  /**
   * Extend the agent's commitment by another 90 days. Backend enforces
   * the 30-day eligibility window. Returns { renewed, zip_code,
   * new_cancel_at }.
   */
  renew: () => authedRequest('/billing/renew', {
    method: 'POST',
    body: JSON.stringify({}),
  }),
};


// ── Letters / Stannp integration ─────────────────────────────────
// Direct-mail letter sending via Stannp, plus print-to-PDF and balance
// management. Charges are set by the backend (backend/api/letters.py
// SINGLE_LETTER_COST_CENTS / SEQUENCE_COST_CENTS); this file does not
// restate them.
//
// archetype_key: pass the same archetype the SixLettersModal previewed
// (ParcelDossierV2 → detectArchetype(...).key) so the server mails the
// agent's voice letter for that archetype — the letter the agent saw.
// Responses carry letter_source ('agent_voice' | 'template'),
// letter_source_note (why the template was used) and recipient_source
// ('owner_mailing' | 'property').

export const letters = {
  /**
   * Render HTML for one letter without sending. Returns {html, letter}.
   * Used by SixLettersModal to show a preview before send.
   */
  preview: (pin, letter_index, archetype_key = null) => authedRequest('/letters/preview', {
    method: 'POST',
    body: JSON.stringify({ pin, letter_index, archetype_key }),
  }),

  /**
   * Send one letter via Stannp. Returns {ok, letter_row_id,
   * stannp_letter_id, stannp_mode, status, cost_cents, new_balance_cents,
   * letter_source, archetype_key, letter_source_note, recipient_source}.
   * Rejects with 402 if balance insufficient, 400 if profile missing
   * phone/return-address fields or no deliverable address.
   */
  send: (pin, letter_index, archetype_key = null) => authedRequest('/letters/send', {
    method: 'POST',
    body: JSON.stringify({ pin, letter_index, archetype_key }),
  }),

  /**
   * Start the 6-letter campaign. Letter 1 mails immediately; 2-6 are
   * scheduled at +30/60/90/135/180 days. Returns {ok, sequence_id,
   * letters_scheduled, cost_cents, stannp_mode, letter_source, ...}.
   * 409 if a campaign is already active for this lead.
   */
  startSequence: (pin, archetype_key = null) => authedRequest('/letters/start-sequence', {
    method: 'POST',
    body: JSON.stringify({ pin, archetype_key }),
  }),

  /**
   * Cancel any unmailed letters in a sequence. Refunds proportionally
   * (cancelled_count / 6 of the sequence charge) back to balance.
   */
  cancelSequence: (sequence_id) => authedRequest(`/letters/cancel-sequence/${sequence_id}`, {
    method: 'POST',
  }),

  /** Get agent's current credit balance. Returns {balance_cents, balance_display}. */
  balance: () => authedRequest('/letters/balance'),

  /** Top-up: backend returns 501 until metered billing ships. */
  topup: () => authedRequest('/letters/topup', { method: 'POST' }),

  /**
   * All letters + sequences for one parcel from this agent. Used by
   * the dossier to show what's been sent and prevent double-sends.
   * Returns {letters: [...], sequences: [...]}.
   */
  byParcel: (pin) => authedRequest(`/letters/by-parcel/${pin}`),

  /**
   * Agent-wide view: every sequence (and standalone single send) for
   * the calling agent. Powers the /letters page. Returns:
   *   {
   *     sequences: [
   *       { sequence_id, is_standalone, pin, address, owner_name,
   *         city, state, started_at, status, total_charged_cents,
   *         letters_total, letters_sent, letters_delivered,
   *         letters_scheduled, letters_returned, letters_failed,
   *         letters_cancelled, latest_event_status, latest_event_at,
   *         next_scheduled_at, letters: [...] }
   *     ],
   *     filter_counts: { active, completed, cancelled, has_delivered,
   *                      has_returned, has_failed, scheduled_pending },
   *     totals: { total_sequences, total_letters, total_sent,
   *               total_delivered, total_scheduled, total_returned,
   *               total_charged_cents }
   *   }
   * Sorted by started_at desc (most recent first).
   */
  sequencesByAgent: () => authedRequest('/letters/sequences-by-agent'),

  /**
   * Render a letter as standalone HTML. Returns the raw HTML string
   * (not JSON) so the frontend can open it in a new window for the
   * browser's Print > Save as PDF flow. Logs a 'pdf_rendered' event.
   * Doesn't deduct balance.
   */
  renderPdfUrl: (pin, letter_index, archetype_key = null) => {
    // Returns the URL for window.open — actual auth handled by browser
    // sending the supabase cookie. We POST through fetch and convert
    // to a blob URL so the new window inherits auth without exposing
    // the JWT in URL params.
    return authedRequest(`/letters/render-pdf/${pin}`, {
      method: 'POST',
      body: JSON.stringify({ letter_index, archetype_key }),
    });
  },
};


// ── V4 map data (MIGRATION_V4.md Phase 4) ──────────────────────────────
export const mapApi = {
  earthConfig: () => authedRequest('/map/earth-config'),
  lotPolygons: (zip_code) => authedRequest(`/map/${zip_code}/lot-polygons`),
};
